import { useState } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { getDescendantOrgs } from '~/common/use-descendant-orgs'
import { parseIntervalToDays } from '~/common/utils/date-utils'

export const toICSDate = (isoString) => {
  const d = new Date(isoString)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}${m}${day}`
}

export const nextDay = (isoString) => {
  const d = new Date(isoString)
  d.setUTCDate(d.getUTCDate() + 1)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}${m}${day}`
}

export const escapeICS = (str) => str?.replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;') ?? ''

export const buildICS = (tasks, { mode = 'single' } = {}) => {
  const events = tasks
    .filter(t => t.task_window_start)
    .map(task => {
      const typeName = task.task_types?.name ?? 'Task'
      const contextName = task.crop?.name ?? task.organization?.name
      const summary = contextName ? `${typeName} — ${contextName}` : typeName
      const status = task.state === 'cancelled' ? 'CANCELLED' : 'CONFIRMED'
      const dtstart = toICSDate(task.task_window_start)
      let dtend
      if (mode === 'duration') {
        const days = parseIntervalToDays(task.duration)
        if (days > 0) {
          const d = new Date(task.task_window_start)
          d.setUTCDate(d.getUTCDate() + days)
          const y = d.getUTCFullYear()
          const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
          const da = String(d.getUTCDate()).padStart(2, '0')
          dtend = `${y}${mo}${da}`
        } else {
          dtend = nextDay(task.task_window_start)
        }
      } else {
        dtend = nextDay(task.task_window_start)
      }

      const lines = [
        'BEGIN:VEVENT',
        `UID:${task.id}@terra-planner`,
        `DTSTART;VALUE=DATE:${dtstart}`,
        `DTEND;VALUE=DATE:${dtend}`,
        `SUMMARY:${escapeICS(summary)}`,
        `STATUS:${status}`,
      ]
      if (task.notes) lines.push(`DESCRIPTION:${escapeICS(task.notes)}`)
      lines.push('END:VEVENT')
      return lines.join('\r\n')
    })

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Terra Planner//Tasks//EN',
    'CALSCALE:GREGORIAN',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n')
}

export const useTasksIcsExport = ({ farmId }) => {
  const [loading, setLoading] = useState(false)

  const exportToICS = async () => {
    if (!farmId) return
    setLoading(true)
    try {
      const descendantIds = await getDescendantOrgs(farmId, 'farm')
      const allOrgIds = [farmId, ...descendantIds]

      const { data, error } = await supabaseGetter.tasks.getByOrganizations(allOrgIds)
      if (error) throw error

      const icsContent = buildICS(data || [])
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'tasks.ics'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('ICS export failed:', err)
    } finally {
      setLoading(false)
    }
  }

  return { exportToICS, loading }
}
